import React from "react";
import { KEY_ESC } from "../../lib/keyboard";
import { setPositionInContentEditable } from "../../lib/selection";
import { eventEmitter } from "../../services";
import { LTRContainer, LTR, LTRFragmented } from "./styles";
import {
    EVT_LTR_MOUSEOVER,
    EVT_LTR_MOUSEOUT,
    EVT_RTL_MOUSEOVER,
    EVT_RTL_MOUSEOUT,
    EVT_LTR_TRANSLATED,
    setFocusClass,
} from "./helpers";

export const DEFAULT_EN = "Enter some text in english to translate.";
// export const DEFAULT_EN = 'Who has been given wisdom?'

type Props = {
    src: string[];
    onInput: (text: string) => void;
};
type State = {
    fragment: boolean;
};

export default class LTRView extends React.Component<Props, State> {
    private elm: React.RefObject<HTMLDivElement>;
    private elmFragmented: React.RefObject<HTMLDivElement>;

    constructor(props: Props) {
        super(props);
        this.state = {
            fragment: false,
        };

        this.elmFragmented = React.createRef<HTMLDivElement>();
        this.elm = React.createRef<HTMLDivElement>();
        this.setupPasteEvt();
    }

    componentDidMount() {
        if (this.elm && this.elm.current) {
            this.props.onInput(this.elm.current.innerText);
        }
        this.setState({ fragment: true });

        eventEmitter.on(EVT_RTL_MOUSEOVER, (index: number) => {
            if (this.elmFragmented && this.elmFragmented.current) {
                setFocusClass(this.elmFragmented.current, index, true);
            }
        });

        eventEmitter.on(EVT_RTL_MOUSEOUT, (index: number) => {
            if (this.elmFragmented && this.elmFragmented.current) {
                setFocusClass(this.elmFragmented.current, index, false);
            }
        });

        eventEmitter.on(EVT_LTR_TRANSLATED, () => {
            this.setState({ fragment: true });
        });
    }

    html() {
        return {
            __html: DEFAULT_EN,
        };
    }

    onBlur = () => {
        if (!this.elm || !this.elm.current) {
            return;
        }

        const e = this.elm.current;
        if (e.innerText.length > 10) {
            this.props.onInput(e.innerText);
            e.innerHTML = e.innerText;
        }
    };

    onKeyUp = (evt: React.KeyboardEvent<HTMLDivElement>) => {
        if (evt.key === KEY_ESC) {
            this.elm && this.elm.current && this.elm.current.blur();
        }
    };

    onMouseOut = (index: number) => eventEmitter.emit(EVT_LTR_MOUSEOUT, index);
    onMouseOver = (index: number) =>
        eventEmitter.emit(EVT_LTR_MOUSEOVER, index);

    onClick = (
        evt: React.MouseEvent<HTMLDivElement>,
        text: string,
        index: number
    ) => {
        const { clientX } = evt;
        const {
            left,
            width,
            height,
        } = (evt.target as HTMLElement).getBoundingClientRect();

        this.setState({ fragment: false }, () => {
            if (!this.elm || !this.elm.current) {
                return;
            }

            this.elm.current.focus();

            let { src } = this.props;
            let position = 0;
            for (let i = 0; i < index; i += 1) {
                position += src[i].length;
            }

            const LINE_HEIGHT = 24;
            const diffX = clientX - left;
            if (height <= LINE_HEIGHT) {
                const xPercentage = diffX / width;
                position += text.length * xPercentage;
            } else {
                // multi-line not supported, position cursor at the start of sentence
            }

            setPositionInContentEditable(this.elm.current, position);
        });
    };

    viewFragmented() {
        const { src } = this.props;
        return src.map((text, i) => (
            <div
                key={`src-${i}`}
                onMouseOut={() => this.onMouseOut(i)}
                onMouseOver={() => this.onMouseOver(i)}
                onClick={(evt: React.MouseEvent<HTMLDivElement>) =>
                    this.onClick(evt, text, i)
                }
            >
                {text}
            </div>
        ));
    }

    onPaste = (evt: ClipboardEvent) => {
        evt.preventDefault();
        if (evt.clipboardData) {
            const text = evt.clipboardData.getData("text/plain");
            document.execCommand("insertText", false, text);
        }
    };

    setupPasteEvt = () => {
        if (!this.elm || !this.elm.current) {
            return;
        }

        this.elm.current.removeEventListener("paste", this.onPaste);
        this.elm.current.addEventListener("paste", this.onPaste);
    };

    render() {
        const { fragment } = this.state;
        return (
            <LTRContainer>
                <LTRFragmented
                    ref={this.elmFragmented}
                    display={fragment ? "block" : "none"}
                >
                    {this.viewFragmented()}
                </LTRFragmented>
                <LTR display={fragment ? "none" : "block"}>
                    <div
                        ref={this.elm}
                        onBlur={this.onBlur}
                        onKeyUp={this.onKeyUp}
                        contentEditable={true}
                        dangerouslySetInnerHTML={this.html()}
                    />
                </LTR>
            </LTRContainer>
        );
    }
}
